import { useQuery } from "@tanstack/react-query";

import { CouponsQueryKeys } from "./couponsQueryKeys";
import { getCashoutOffers } from "@/api/coupons";
import { CashoutOffers } from "@/types/ApiResponses/Cashout";
import { chunkCouponIds, getCashoutPollInterval } from "@/utils/cashout";

interface UseCashoutOffersOptions {
  // Only a player or a sub-dealer can cash out their own coupons.
  enabled: boolean;
}

/**
 * Cash-out offers for the given open coupons (selectCashoutCouponIds), one
 * request per 20 ids. Polls every 5 s while the page is visible and backs
 * off when nothing can be offered (getCashoutPollInterval). Always stale: an
 * offer is signed for seconds, so a refocus asks again at once.
 */
export const useCashoutOffers = (
  couponIds: string[],
  { enabled }: UseCashoutOffersOptions
) => {
  return useQuery({
    queryKey: [CouponsQueryKeys.CashoutOffers, couponIds],
    enabled: enabled && couponIds.length > 0,
    staleTime: 0,
    queryFn: async ({ signal }): Promise<CashoutOffers> => {
      const answers = await Promise.all(
        chunkCouponIds(couponIds).map((ids) =>
          getCashoutOffers({ signal, params: { ids } })
        )
      );
      return answers.reduce<CashoutOffers>(
        (offers, answer) => ({ ...offers, ...answer }),
        {}
      );
    },
    refetchInterval: (query) =>
      getCashoutPollInterval(query.state.data, couponIds.length),
  });
};
